// SPDX-FileCopyrightText: Copyright 2026 OP Pohjola (https://op.fi). All Rights Reserved.
// SPDX-License-Identifier: LicenseRef-OpPohjolaAllRightsReserved

import React, { useContext, useEffect, useState } from 'react';
import { ChangeStateContext, StateContext } from '../../State';
import { Button } from '@op/opds.opux';
import { Message } from '@messageformat/react';
import RepresentationList from './representationlist/RepresentationList';
import RepresentationAlert from './RepresentationAlert';
import api, { UnexpectedContentTypeError } from '../../api';
import { RepMode, ErrorCode, LoginType, representationContainerId } from '../../constants';
import '../../css/representation.scss';
import { ModelContext } from '../../ModelContext';

const Representation = (props) => {
    const state = useContext(StateContext);
    const changeState = useContext(ChangeStateContext);
    const [errorsInRepresentation, setErrorsInRepresentation] = useState(null);
    const { model } = useContext(ModelContext);

    const selectDefaultRepresentation = async () => {
        let selectedRep = null;
        if (model.representationServiceFilter) {
            selectedRep = await api.selectRepresentationByServiceFilter(model.representationServiceFilter);
        } else {
            selectedRep = await api.selectRepresentationByTypes(model.representationTypes);
        }
        if (selectedRep?.representationType) {
            return selectedRep;
        }
        return null;
    };

    const selectRepresentationFromList = () => {
        getRepresentationList()
            .then((response) => {
                if (response?.representations.length > 0) {
                    console.debug('representations', response.representations);
                    setErrorsInRepresentation(response?.errors);
                    if (model.loginType !== LoginType.CHANGEREP && !response?.errors && response.representations.length === 1) {
                        /* If we have exactly one representation, select it automatically unless we are in change representation flow */
                        selectRepresentationAndRedirectToSuccessURL(response.representations[0]);
                    } else {
                        /* Otherwise show the list from which user can choose the correct representation */
                        changeState({ repMode: RepMode.LIST, representations: response.representations });
                    }
                } else {
                    setError(ErrorCode.REPRESENTATIONS_NOT_FOUND);
                }
            })
            .catch((error) => {
                console.debug('getting representation list failed:', error.response);
                /* Special logic because eai-common returns http 500 with this message when it finds 0 representations. */
                const errors = error?.response?.data?.errors;
                if (Array.isArray(errors) && errors.length > 0 && errors[0].message === 'No representations could be selected!') {
                    setError(ErrorCode.REPRESENTATIONS_NOT_FOUND);
                } else {
                    setError(ErrorCode.REPRESENTATION_LIST_FAILED);
                }
            });
    };

    const selectAlertToShow = () => {
        if (model.loginType !== LoginType.OPLOGIN && model.loginType !== LoginType.CHANGEREP) {
            /* Show application error for example /representation*/
            /* Show error on /representation according to availability of errorsInRepresentation*/
            return state.errorCode;
        }
        return errorsInRepresentation ? ErrorCode.REPRESENTATIONS_HAS_ERROR : state.errorCode;
    };

    const selectRepresentationAndRedirectToSuccessURL = async (rep) => {
        console.debug('selecting representation:', rep);
        return api
            .selectRepresentation(rep, model.representationServiceFilter)
            .then((selectedRep) => {
                if (selectedRep?.representationType) {
                    console.debug('select representation response:', selectedRep);
                    redirectToSuccessURL();
                } else {
                    console.debug('select representation bad response:', selectedRep);
                    setError(ErrorCode.SELECT_REPRESENTATION_FAILED);
                }
            })
            .catch((e) => {
                console.debug('select representation failed:', e);
                setError(ErrorCode.SELECT_REPRESENTATION_FAILED);
            });
    };

    const setError = (errorCode) => {
        changeState({ errorCode: errorCode });
    };

    const isRepTypeSupported = (repType) => {
        return model.representationTypes.includes(repType);
    };

    const getRepresentationList = async () => {
        if (model.representationServiceFilter) {
            return api.getRepresentationListByServiceFilter(model.representationServiceFilter);
        } else {
            return api.getRepresentationListByTypes(model.representationTypes);
        }
    };

    const redirectToSuccessURL = () => {
        console.debug('redirecting to success url:', model.successUrl);
        window.location.href = model.successUrl;
    };

    const onBackButtonClicked = () => {
        console.debug('representation back button clicked');
        if (state.errorCode && model.errorUrl) {
            console.debug('redirecting to error url:', model.errorUrl);
            window.location.href = model.errorUrl;
        } else {
            redirectToSuccessURL();
        }
    };

    const redirectToLoginUrl = () => {
        const url = new URL(window.location.href);
        url.pathname = url.pathname
            .split('/')
            .filter((segment) => segment !== 'representation')
            .join('/');
        url.searchParams.set('TAM_OP', 'login');
        url.searchParams.set('ERROR_CODE', '0x00000000');
        const hostname = url.searchParams.get('hostname');
        if (hostname !== null) {
            url.searchParams.delete('hostname');
            url.searchParams.set('HOSTNAME', hostname);
        }
        const successUrl = url.searchParams.get('successUrl');
        if (successUrl !== null) {
            url.searchParams.delete('successUrl');
            url.searchParams.set('URL', successUrl);
        }
        console.debug('Redirecting to login from representation selection because of authlevel');
        window.location.href = url.toString();
    };

    const performRepresentationCalls = () => {
        if (state.errorCode && state.repMode !== RepMode.ERROR) {
            console.debug('representation error:', state.errorCode);
            if (model.loginType !== LoginType.OPLOGIN) {
                changeState({ repMode: RepMode.ERROR });
                /*
                  If we are in representation selection from ftn or idkaart, we need to logout and set mode to error.
                  This way going to original success url will trigger new login prompt and user can try again.
                */
                if (model.loginType !== LoginType.CHANGEREP) {
                    console.debug('calling logout after representation error');
                    api.logout().catch((e) => {
                        console.debug('representation error logout failed:', e);
                    });
                }
            }
        } else if (state.repMode === RepMode.AUTO_SELECT) {
            if (isRepTypeSupported('NONE')) {
                /* If NONE type is supported, let's go straight to success URL */
                redirectToSuccessURL();
            } else {
                let defaultRep = null;
                let contentError = null;
                selectDefaultRepresentation()
                    .then((rep) => {
                        console.debug('select default representation result:', rep);
                        defaultRep = rep;
                    })
                    .catch((e) => {
                        /* This error is not considered fatal and we continue the flow in the finally block except
                         * for content error which much likely indicate WAF interception and can lead to very strange behaviors */
                        console.debug('selecting default representation failed:', e);
                        if (e instanceof UnexpectedContentTypeError) {
                            contentError = e;
                        }
                    })
                    .finally(() => {
                        /* If valid default rep found, we can forward to success URL without fetching the list */
                        if (contentError) {
                            setError(ErrorCode.SELECT_REPRESENTATION_FAILED);
                        } else if (defaultRep) {
                            redirectToSuccessURL();
                        } else {
                            selectRepresentationFromList();
                        }
                    });
            }
        } else if (state.repMode === RepMode.CHANGE_REP_INIT) {
            selectRepresentationFromList();
        }
    };

    useEffect(() => {
        /* We only perform representation selection if we are finished with authentication */
        if (state.currentAuthLevel < model.authLevel || state.currentAuthLevel < 2) {
            /* If we are not finished with authentication we need to guide the user back to perform the authentication */
            console.log('authLevelAtPageLoad: ', model.authLevelAtPageLoad);
            redirectToLoginUrl();
        } else {
            performRepresentationCalls();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div id={representationContainerId} className='opux-v6__opds' aria-hidden='false'>
            {(state.errorCode || errorsInRepresentation) && <RepresentationAlert errorCode={selectAlertToShow()} />}
            {state.repMode === RepMode.LIST && (
                <RepresentationList
                    representations={state.representations}
                    selectRepresentation={selectRepresentationAndRedirectToSuccessURL}
                    onBackButtonClicked={onBackButtonClicked}
                />
            )}
            {model.loginType !== LoginType.OPLOGIN && state.errorCode && (
                <Button onClick={onBackButtonClicked}>
                    <Message id='fp.representation.back' />
                </Button>
            )}
        </div>
    );
};

export default Representation;
